import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";

import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import "../fonts/breuer-headline.css";
import "./raceStage.css";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
} from "../../functions/stageReportSync";
import { flushLayoutFrame } from "../../functions/stageCelebration";
import {
  fireStageHelpSwal,
  getRaceHowToPlayRules,
} from "../../functions/stageRules";
import {
  validateThemeStageData,
  fireThemeStageDataMissingSwal,
} from "../../functions/validateThemeStageData";
import {
  RACE_DEFAULT_SPEED,
  RACE_OBSTACLE_COUNT,
  NITRO_MAX,
  NITRO_START,
  NITRO_DRAIN_PER_SEC,
  NITRO_REGEN_PER_SEC,
  NITRO_GAIN_ON_OBSTACLE,
  NITRO_SPEED_MULTIPLIER,
  ENDLESS_SPEED_RAMP_PER_SEC,
  ENDLESS_MAX_SPEED_BONUS,
  ENDLESS_SPAWN_BASE,
  ENDLESS_SPAWN_MIN,
  ENDLESS_SPAWN_RAMP_PER_SEC,
  getEndlessDifficultyProfile,
  QUIZ_WRONG_SLOW_FACTOR,
  QUIZ_WRONG_SLOW_MS,
  GAME_MODE_QUIZ,
  computeEndlessScore,
  getDistanceMultiplier,
  getObstaclePoints,
  getQuizPoints,
  getEndlessMaxHits,
  getEndlessLifelinePlays,
  getQuestionOnObstacle,
  getQuestionOnPoints,
  isEndlessMode,
  parseQuizFromTheme,
  parseRaceAssetsFromTheme,
  pickRandom,
} from "./raceGameUtils";
import {
  playBoostSound,
  playCountdownSound,
  playCrashSound,
  setEngineSoundSrc,
  startEngineSound,
  stopAllRaceSounds,
  stopEngineSound,
  unlockRaceAudio,
  updateEngineSound,
} from "./raceSounds";
import { allowScreenSleep, keepScreenAwake } from "./keepScreenAwake";
import {
  DIFFICULTY_SPEED,
  DIFFICULTY_STORAGE_KEY,
  GAME_MODE_STORAGE_KEY,
  MOBILE_CONTROL_ARROWS,
  MOBILE_CONTROL_MOTION,
  MOBILE_CONTROL_OPTIONS,
  MOBILE_CONTROL_SWIPE,
  SELECTED_CAR_STORAGE_KEY,
  readMobileControlMode,
  readSessionValue,
  writeSessionValue,
  MOBILE_CONTROL_STORAGE_KEY,
} from "../gameSetupStorage";
import { setBackButtonUrl } from "../uiSlice";
import {
  darkenThemeHexColor,
  getDvSourceImageUrl,
  getLandingPageTitleColor,
  getThemeButtonGradient,
  getThemeButtonTextColor,
} from "../../functions/themeAssets";
import timeHudIcon from "../img/time.png";
import pointsHudIcon from "../img/points.png";
import rulesHudIcon from "../img/rules.png";
import livesHudIcon from "../img/lifeline.png";

const STAGE_NUM = 1;
/** DB score columns: stages.stage1 = quiz score, stages.stage2 = endless best run. */
const QUIZ_SCORE_STAGE = 1;
const ENDLESS_SCORE_STAGE = 2;
const CRASH_SMOKE_MS = 2200;
const CRASH_END_DELAY_MS = 950;
const DEMO_PLANS_URL = "https://extramileplay.com/plans";
const DEMO_REDIRECT_MS = 10000;
/** Quiz mode: finish banner spawns this far above the track after the last answer. */
const FINISH_LINE_SPAWN_Y = -560;

/** Mobile portrait: swipe + device tilt. */
const SWIPE_STEER_PX = 28;
const SWIPE_NITRO_DY = 48;
/** Snap car into a lane (0–2) instead of analog crawl. */
const LANE_SNAP_LERP = 0.22;
const LANE_ARRIVE_PX = 3;
/** Motion steer: milder than before so tilt is controllable. */
const TILT_DEADZONE_DEG = 2;
const TILT_FULL_DEG = 16;
/** Per-frame lerp toward tilt target (game loop @ ~60fps). */
const TILT_SMOOTH = 0.28;
const TILT_STEER_MULT = 1.55;

/** Obstacle traffic: forward-only speed variety (no left/right movement). */
const OBSTACLE_WIDTH_FALLBACK = 50;
const TRAFFIC_SPEED_MIN = 0.5;
const TRAFFIC_SPEED_MAX = 0.92;
const STATIC_HAZARD_CHANCE = 0.3;
const SPAWN_CLEAR_Y = 240;
const SPAWN_CLEAR_X_PAD = 28;
const OBSTACLE_LANES = 3;
const SPAWN_PLACE_ATTEMPTS = 28;
/** Vertical window where all lanes blocked = unwinnable wall. */
const PASS_BAND_Y = 210;
/**
 * Keep new obstacles off-screen long enough to react at medium/hard speeds.
 * Speed mul ≈ frames of travel at full hazard speed (~1.8s @ 60fps).
 */
const SPAWN_MIN_VIEW_MUL = 1.85;
const SPAWN_MIN_SPEED_MUL = 125;
const SPAWN_ONSCREEN_CAP = 0.85;
/** Spawns this close (or closer) must keep the player's lane clear. */
const SPAWN_AVOID_LANE_VIEW_MUL = 1.35;
/** Don't laterally shove traffic already this close to the player. */
const NEAR_PLAYER_Y = 720;
/**
 * Reaction guarantee: spawning far away is not enough, because the closing
 * speed on screen is what decides whether a car "appears" in your face.
 * Traffic never closes the visible gap faster than this many seconds.
 */
const MIN_APPROACH_SEC = 1.6;
/** Fast runs may tighten the window down to here, never below. */
const MIN_APPROACH_SEC_FLOOR = 1.2;
const APPROACH_FPS = 60;
/** Start limiting closing speed slightly before the car enters the view. */
const OBSTACLE_ENTER_MARGIN = 140;

function randBetween(min, max) {
  return min + Math.random() * (max - min);
}

let activeEndlessProfile = getEndlessDifficultyProfile("medium");

function randomizeObstacleMotion(item, profile) {
  const p = profile || activeEndlessProfile;
  const staticChance = p?.staticChance ?? STATIC_HAZARD_CHANCE;
  const trafficMin = p?.trafficMin ?? TRAFFIC_SPEED_MIN;
  const trafficMax = p?.trafficMax ?? TRAFFIC_SPEED_MAX;
  item.speedFactor =
    Math.random() < staticChance
      ? 1
      : randBetween(trafficMin, trafficMax);
}

function laneLeft(track, lane, width) {
  const trackW = track.clientWidth;
  const laneW = trackW / OBSTACLE_LANES;
  const clamped = Math.max(0, Math.min(OBSTACLE_LANES - 1, lane));
  const x = clamped * laneW + (laneW - width) / 2;
  return Math.max(0, Math.min(Math.max(0, trackW - width), x));
}

function clampLane(lane) {
  return Math.max(0, Math.min(OBSTACLE_LANES - 1, lane));
}

/**
 * Change at most one lane from the settled position.
 * While the car is still sliding, extra swipes are ignored so it cannot
 * skip the center (left ⇄ right in one gesture).
 */
function requestLaneStep(player, dir) {
  if (!player) return;
  const step = dir < 0 ? -1 : 1;
  const settled = clampLane(player.lane ?? 1);
  const target = clampLane(player.targetLane ?? settled);
  if (target !== settled) return;
  player.targetLane = clampLane(settled + step);
}

function inferLane(item, track) {
  if (item.lane != null && item.lane >= 0 && item.lane < OBSTACLE_LANES) {
    return item.lane;
  }
  const width = item.offsetWidth || OBSTACLE_WIDTH_FALLBACK;
  const laneW = Math.max(1, track.clientWidth / OBSTACLE_LANES);
  const center = (item.x || 0) + width / 2;
  return Math.max(0, Math.min(OBSTACLE_LANES - 1, Math.floor(center / laneW)));
}

function lanesOccupiedInBand(y, others, ignore, track, bandY = PASS_BAND_Y) {
  const occupied = new Set();
  for (let i = 0; i < others.length; i += 1) {
    const other = others[i];
    if (other === ignore) continue;
    if (Math.abs((other.y || 0) - y) < bandY) {
      occupied.add(inferLane(other, track));
    }
  }
  return occupied;
}

function freeLanesInBand(y, others, ignore, track) {
  const occupied = lanesOccupiedInBand(y, others, ignore, track);
  const free = [];
  for (let lane = 0; lane < OBSTACLE_LANES; lane += 1) {
    if (!occupied.has(lane)) free.push(lane);
  }
  return free;
}

function obstaclesClash(itemY, left, width, others, ignore) {
  for (let i = 0; i < others.length; i += 1) {
    const other = others[i];
    if (other === ignore) continue;
    if (
      Math.abs((other.y || 0) - itemY) < SPAWN_CLEAR_Y &&
      Math.abs((other.x || 0) - left) < width + SPAWN_CLEAR_X_PAD
    ) {
      return true;
    }
  }
  return false;
}

function pickClearLane(item, track, others, avoidLane) {
  const free = freeLanesInBand(item.y || 0, others, item, track);
  const safer =
    avoidLane == null ? free : free.filter((lane) => lane !== avoidLane);
  const pool = safer.length ? safer : free;
  // Never fill the last open lane in a band — keep a passable gap.
  if (pool.length > 0) {
    return pool[Math.floor(Math.random() * pool.length)];
  }

  const width = item.offsetWidth || OBSTACLE_WIDTH_FALLBACK;
  let bestLane = Math.floor(Math.random() * OBSTACLE_LANES);
  let bestDist = -1;
  const lanesToScan = [];
  for (let lane = 0; lane < OBSTACLE_LANES; lane += 1) {
    if (avoidLane != null && lane === avoidLane) continue;
    lanesToScan.push(lane);
  }
  if (!lanesToScan.length) {
    for (let lane = 0; lane < OBSTACLE_LANES; lane += 1) lanesToScan.push(lane);
  }

  for (let n = 0; n < lanesToScan.length; n += 1) {
    const lane = lanesToScan[n];
    const left = laneLeft(track, lane, width);
    let nearest = Infinity;
    for (let i = 0; i < others.length; i += 1) {
      const other = others[i];
      if (other === item) continue;
      if (Math.abs((other.x || 0) - left) < width + SPAWN_CLEAR_X_PAD) {
        nearest = Math.min(nearest, Math.abs((other.y || 0) - (item.y || 0)));
      }
    }
    const score = nearest === Infinity ? 10000 : nearest;
    if (score > bestDist) {
      bestDist = score;
      bestLane = lane;
    }
  }
  return bestLane;
}

function minSpawnLeadY(track, speed, spawnGap) {
  const view = track.clientHeight || 700;
  return Math.max(
    view * SPAWN_MIN_VIEW_MUL,
    (Number(speed) || RACE_DEFAULT_SPEED) * SPAWN_MIN_SPEED_MUL,
    Number(spawnGap) || ENDLESS_SPAWN_BASE,
    560
  );
}

function clampSpawnOffscreen(track, y) {
  const view = track.clientHeight || 700;
  const cap = -Math.max(180, view * SPAWN_ONSCREEN_CAP);
  return y > cap ? cap - Math.random() * 90 : y;
}

/**
 * Max px/frame an obstacle may travel down the screen. Based on the visible
 * gap above the car, so the player always gets MIN_APPROACH_SEC to react no
 * matter how fast the race is running.
 */
function maxApproachStep(track, speed, profile) {
  const view = track.clientHeight || 700;
  const visibleLead = Math.max(220, view * 0.62);
  const over = Math.max(
    0,
    (Number(speed) || RACE_DEFAULT_SPEED) - RACE_DEFAULT_SPEED
  );
  const baseSec = profile?.approachSec ?? MIN_APPROACH_SEC;
  const floorSec = profile?.approachFloor ?? MIN_APPROACH_SEC_FLOOR;
  const sec = Math.max(floorSec, baseSec - over * 0.04);
  return visibleLead / (sec * APPROACH_FPS);
}

/** At/below this y a car is visible, so lane teleports look like cheating. */
function laneLockY(playerY) {
  return Math.min(-OBSTACLE_ENTER_MARGIN, (playerY ?? 500) - NEAR_PLAYER_Y);
}

/** Traffic this close must keep its lane — no sideways pops in front of the car. */
function trafficMustHoldLane(itemY, playerY, track) {
  const view = track?.clientHeight || 700;
  const y = Number(itemY) || 0;
  const carY = Number(playerY) || view * 0.72;
  return y > -view * 1.3 || y > carY - view * 1.2;
}

function lanesAwayFromPlayer(free, avoidLane) {
  if (avoidLane == null || avoidLane < 0 || avoidLane >= OBSTACLE_LANES) {
    return free;
  }
  const safer = free.filter((lane) => lane !== avoidLane);
  return safer.length ? safer : free;
}

/**
 * Spawns far above the view give plenty of reaction time, so every lane —
 * including the one the player is sitting in — must stay in rotation.
 * Only a spawn close to the visible area keeps the player's lane clear.
 */
function spawnIsNearView(item, track) {
  const view = track.clientHeight || 700;
  return (item.y || 0) > -view * SPAWN_AVOID_LANE_VIEW_MUL;
}

function placeObstacle(item, track, others, avoidLane) {
  const width = item.offsetWidth || OBSTACLE_WIDTH_FALLBACK;
  const maxLeft = Math.max(0, track.clientWidth - width);

  // Push further up until at least one lane stays open in this band.
  for (let lift = 0; lift < 12; lift += 1) {
    if (freeLanesInBand(item.y || 0, others, item, track).length > 0) break;
    item.y = (item.y || 0) - PASS_BAND_Y * 0.55;
    item.style.top = `${item.y}px`;
  }

  const view = track.clientHeight || 700;
  const farEnough = (item.y || 0) < -view * 1.85;
  const respectAvoid =
    avoidLane != null &&
    avoidLane >= 0 &&
    avoidLane < OBSTACLE_LANES &&
    !farEnough;

  let lane = pickClearLane(item, track, others, respectAvoid ? avoidLane : null);
  if (respectAvoid) {
    const free = lanesAwayFromPlayer(
      freeLanesInBand(item.y || 0, others, item, track),
      avoidLane
    );
    if (free.length) {
      lane = free[Math.floor(Math.random() * free.length)];
    }
  }
  let left = laneLeft(track, lane, width);

  for (let attempt = 0; attempt < SPAWN_PLACE_ATTEMPTS; attempt += 1) {
    const open = freeLanesInBand(item.y || 0, others, item, track);
    const free = respectAvoid ? lanesAwayFromPlayer(open, avoidLane) : open;
    const laneOk = free.includes(lane) || free.length === 0;
    if (laneOk && !obstaclesClash(item.y || 0, left, width, others, item)) {
      break;
    }
    if (free.length) {
      lane = free[attempt % free.length];
    } else {
      lane = (lane + 1) % OBSTACLE_LANES;
      item.y = (item.y || 0) - SPAWN_CLEAR_Y * 0.45;
      item.style.top = `${item.y}px`;
    }
    left = laneLeft(track, lane, width);
    if (attempt > OBSTACLE_LANES * 4) {
      const jitter = (Math.random() - 0.5) * (width * 0.35);
      left = Math.min(maxLeft, Math.max(0, left + jitter));
    }
  }

  item.lane = lane;
  item.x = Math.min(maxLeft, Math.max(0, left));
  item.style.left = `${item.x}px`;
}

function respawnObstacle(item, track, others, spawnGap, speed, avoidLane) {
  item.collided = false;
  let y =
    -minSpawnLeadY(track, speed, spawnGap) - Math.random() * 200;
  for (let attempt = 0; attempt < 16; attempt += 1) {
    let tooClose = false;
    for (let i = 0; i < others.length; i += 1) {
      const other = others[i];
      if (other === item) continue;
      if (Math.abs((other.y || 0) - y) < SPAWN_CLEAR_Y) {
        tooClose = true;
        break;
      }
    }
    // Also refuse a Y that would seal every lane with nearby traffic.
    if (!tooClose) {
      item.y = y;
      if (freeLanesInBand(y, others, item, track).length === 0) {
        tooClose = true;
      }
    }
    if (!tooClose) break;
    y -= SPAWN_CLEAR_Y * 0.65 + Math.random() * 40;
  }
  const view = track.clientHeight || 700;
  const minOffscreen = -Math.max(view * 1.15, 560);
  y = Math.min(y, minOffscreen);
  item.y = clampSpawnOffscreen(track, y);
  item.style.top = `${item.y}px`;
  placeObstacle(item, track, others, avoidLane);
  randomizeObstacleMotion(item);
}

/**
 * Solid hit: car stays put and gets "stuck" on the bumper.
 * Obstacle is held against the nose until the player steers clear, then it
 * continues down the track. Never teleports obstacles away / never sinks the car.
 * Returns true when overlapping.
 */
function resolvePlayerObstacleSolid(player, car, obstacle, road) {
  const carW = car.offsetWidth;
  const carH = car.offsetHeight;
  const obW = obstacle.offsetWidth || OBSTACLE_WIDTH_FALLBACK;
  const obH = obstacle.offsetHeight || OBSTACLE_WIDTH_FALLBACK;
  const carL = player.x;
  const carT = player.y;
  const carR = carL + carW;
  const carB = carT + carH;
  const obL = obstacle.x ?? 0;
  const obT = obstacle.y ?? 0;
  const obR = obL + obW;
  const obB = obT + obH;

  if (carR <= obL || carL >= obR || carB <= obT || carT >= obB) return false;

  const prevY = player.y;
  const maxX = Math.max(0, road.width - carW);
  const maxY = Math.max(40, road.height - carH - 20);

  const penLeft = carR - obL;
  const penRight = obR - carL;
  const penTop = carB - obT;
  const penBottom = obB - carT;
  const minX = Math.min(penLeft, penRight);
  const minY = Math.min(penTop, penBottom);

  if (minX < minY * 0.9) {
    // Side scrape — only slide the player sideways so they can slip past.
    if (penLeft < penRight) player.x -= penLeft;
    else player.x += penRight;
    player.x = Math.min(maxX, Math.max(0, player.x));
    return true;
  }

  // Bumper lock: hold the obstacle just ahead of the car (don't delete / fling it).
  const bumperLockY = prevY - obH - 2;
  obstacle.y = Math.min(obT, bumperLockY);
  obstacle.style.top = `${obstacle.y}px`;

  // Block driving forward through the obstacle; never shove the car downward.
  const minPlayerY = obstacle.y + obH + 2;
  if (player.y < minPlayerY) player.y = minPlayerY;
  if (player.y > prevY) player.y = prevY;

  player.x = Math.min(maxX, Math.max(0, player.x));
  player.y = Math.min(maxY, Math.max(40, player.y));
  return true;
}

/** Keep traffic from stacking when speeds differ mid-run. */
function separateOverlappingObstacles(obstacles, track, avoidLane, playerY) {
  const widthFallback = OBSTACLE_WIDTH_FALLBACK;
  const list = Array.from(obstacles);
  const dangerY = laneLockY(playerY);
  for (let i = 0; i < list.length; i += 1) {
    for (let j = i + 1; j < list.length; j += 1) {
      const a = list[i];
      const b = list[j];
      const aw = a.offsetWidth || widthFallback;
      const bw = b.offsetWidth || widthFallback;
      const minX = (aw + bw) / 2 + SPAWN_CLEAR_X_PAD * 0.5;
      if (Math.abs((a.y || 0) - (b.y || 0)) >= SPAWN_CLEAR_Y * 0.7) continue;
      if (Math.abs((a.x || 0) - (b.x || 0)) >= minX) continue;

      // Prefer different lanes; push the rear car (higher y) sideways.
      const rear = (a.y || 0) >= (b.y || 0) ? a : b;
      const front = rear === a ? b : a;
      const holdLane = trafficMustHoldLane(rear.y, playerY, track);
      // Close traffic must never hop sideways into the player's path.
      if (holdLane) {
        rear.y = (front.y || 0) - PASS_BAND_Y * 1.2;
        rear.style.top = `${rear.y}px`;
        continue;
      }
      const nearPlayer = (rear.y || 0) >= dangerY;
      const open = freeLanesInBand(rear.y || 0, list, rear, track).filter(
        (lane) => lane !== inferLane(front, track)
      );
      const free = nearPlayer
        ? open.filter((lane) => avoidLane == null || lane !== avoidLane)
        : open;
      let lane;
      if (free.length) {
        lane = free[Math.floor(Math.random() * free.length)];
      } else {
        // No sideways escape — open a vertical gap instead of teleporting
        // into the player's lane.
        rear.y = (front.y || 0) - PASS_BAND_Y * (nearPlayer ? 1.15 : 0.95);
        rear.style.top = `${rear.y}px`;
        const lifted = freeLanesInBand(rear.y || 0, list, rear, track).filter(
          (l) => l !== inferLane(front, track)
        );
        const safe = nearPlayer
          ? lifted.filter((l) => avoidLane == null || l !== avoidLane)
          : lifted;
        if (safe.length) {
          lane = safe[Math.floor(Math.random() * safe.length)];
        } else if (nearPlayer) {
          // Stay put laterally; the vertical lift already opened a gap.
          continue;
        } else {
          lane = pickClearLane(rear, track, list);
        }
      }
      const width = rear.offsetWidth || widthFallback;
      const maxLeft = Math.max(0, track.clientWidth - width);
      rear.lane = lane;
      rear.x = Math.min(maxLeft, Math.max(0, laneLeft(track, lane, width)));
      rear.style.left = `${rear.x}px`;
      // Also keep a little vertical gap if still overlapping in x.
      if (Math.abs((rear.x || 0) - (front.x || 0)) < minX) {
        rear.y = (front.y || 0) - PASS_BAND_Y * 0.85;
        rear.style.top = `${rear.y}px`;
      }
    }
  }
}

/** Mid-run safety: if any band seals every lane, lift one car out. */
function ensurePassableCorridor(obstacles, track, avoidLane, playerY) {
  const list = Array.from(obstacles);
  const dangerY = laneLockY(playerY);
  for (let i = 0; i < list.length; i += 1) {
    const item = list[i];
    const occupied = lanesOccupiedInBand(item.y || 0, list, null, track);
    if (occupied.size < OBSTACLE_LANES) continue;
    item.y = (item.y || 0) - PASS_BAND_Y * 0.9;
    item.style.top = `${item.y}px`;
    if (trafficMustHoldLane(item.y, playerY, track)) {
      continue;
    }
    const nearPlayer = (item.y || 0) >= dangerY;
    const open = freeLanesInBand(item.y || 0, list, item, track);
    const free = nearPlayer
      ? lanesAwayFromPlayer(open, avoidLane)
      : open;
    let lane = free.length
      ? free[Math.floor(Math.random() * free.length)]
      : pickClearLane(item, track, list);
    // Close to the car: lift further rather than drop into the player's lane.
    if (nearPlayer && avoidLane != null && lane === avoidLane) {
      item.y = (item.y || 0) - PASS_BAND_Y;
      item.style.top = `${item.y}px`;
      const lifted = freeLanesInBand(item.y || 0, list, item, track).filter(
        (l) => l !== avoidLane
      );
      if (lifted.length) {
        lane = lifted[Math.floor(Math.random() * lifted.length)];
      }
    }
    const width = item.offsetWidth || OBSTACLE_WIDTH_FALLBACK;
    const maxLeft = Math.max(0, track.clientWidth - width);
    item.lane = lane;
    item.x = Math.min(maxLeft, Math.max(0, laneLeft(track, lane, width)));
    item.style.left = `${item.x}px`;
  }
}

function spawnFinishLine(track) {
  if (!track) return null;
  const existing = track.querySelector(".race-stage__finish");
  if (existing) existing.remove();

  const el = document.createElement("div");
  el.className = "race-stage__finish";
  el.setAttribute("aria-hidden", "true");

  const banner = document.createElement("div");
  banner.className = "race-stage__finish-banner";
  banner.textContent = "FINISH";
  el.appendChild(banner);

  el.y = FINISH_LINE_SPAWN_Y;
  el.style.top = `${el.y}px`;
  track.appendChild(el);
  return el;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

function shuffle(array) {
  const next = [...array];
  for (let i = next.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [next[i], next[j]] = [next[j], next[i]];
  }
  return next;
}

function readGameMode() {
  return (
    readSessionValue(GAME_MODE_STORAGE_KEY) || GAME_MODE_QUIZ
  );
}

function QuizFlagIcon() {
  return (
    <svg
      className="race-quiz__flag"
      viewBox="0 0 16 12"
      width="18"
      height="14"
      aria-hidden="true"
    >
      <rect width="16" height="12" rx="1" fill="#0b1228" />
      <path
        fill="#f5f5f5"
        d="M0 0h4v3H0zm8 0h4v3H8zM4 3h4v3H4zm8 0h4v3h-4zM0 6h4v3H0zm8 0h4v3H8zM4 9h4v3H4zm8 0h4v3h-4z"
      />
    </svg>
  );
}

/** Stable track host — must not re-render or React wipes DOM-created cars/obstacles. */
const RaceTrack = React.memo(
  React.forwardRef(function RaceTrack(_props, ref) {
    return <div ref={ref} className="race-stage__game-area" />;
  })
);

export default function Stage1() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const [stageRowId, setStageRowId] = useState(
    () => sessionStorage.getItem(STAGE_ROW_KEY) || null
  );
  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage1Points, setStage1Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [bestScore, setBestScore] = useState(0);
  const [endScreen, setEndScreen] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [themeDataBlocked, setThemeDataBlocked] = useState(false);
  const [gameStarted, setGameStarted] = useState(false);
  const [quizOpen, setQuizOpen] = useState(false);
  const [quizQuestion, setQuizQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState("");
  const [raceAlert, setRaceAlert] = useState(null);
  const [gameMode, setGameMode] = useState(() => readGameMode());

  const trackRef = useRef(null);
  const stageRootRef = useRef(null);
  const hudScoreRef = useRef(null);
  const hudTimeRef = useRef(null);
  const hudBestRef = useRef(null);
  const hudDistRef = useRef(null);
  const hudSpeedRef = useRef(null);
  const hudLivesRef = useRef(null);
  const endlessMaxHitsRef = useRef(1);
  const playsUsedRef = useRef(0);
  const maxPlaysRef = useRef(0);
  const nitroRootRef = useRef(null);
  const nitroFillRef = useRef(null);
  const nitroLabelRef = useRef(null);
  const keysRef = useRef({
    ArrowUp: false,
    ArrowDown: false,
    ArrowLeft: false,
    ArrowRight: false,
    w: false,
    W: false,
    a: false,
    A: false,
    s: false,
    S: false,
    d: false,
    D: false,
  });
  const playerRef = useRef({
    start: false,
    speed: RACE_DEFAULT_SPEED,
    x: 0,
    y: 0,
    score: 0,
  });
  const rafRef = useRef(0);
  const hitCountRef = useRef(0);
  const pointsRef = useRef(0);
  const obstaclesForQuestionRef = useRef(0);
  const pointsForQuestionRef = useRef(0);
  const questionIndexRef = useRef(0);
  const questionsAnsweredRef = useRef(0);
  const quizDeckRef = useRef([]);
  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const raceStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);
  const stage1CompleteRef = useRef(false);
  const themeMissingPopupShownRef = useRef(false);
  const assetsRef = useRef({ carUrls: [], obstacleUrls: [] });
  const questionOnObstacleRef = useRef(2);
  const questionOnPointsRef = useRef(0);
  const quizPointsRef = useRef(1);
  const obstaclePointsRef = useRef(1);
  const distanceMultiplierRef = useRef(0.05);
  const raceSpeedRef = useRef(RACE_DEFAULT_SPEED);
  const endlessDiffRef = useRef(getEndlessDifficultyProfile("medium"));
  const selectedCarUrlRef = useRef("");
  const raceAlertResolverRef = useRef(null);
  const userAnswersRef = useRef([]);
  const gameModeRef = useRef(readGameMode());
  const bestScoreRef = useRef(0);
  const distanceRef = useRef(0);
  const obstaclesAvoidedRef = useRef(0);
  /** 0–100 nitro charge. */
  const nitroChargeRef = useRef(NITRO_START);
  /** Player toggled nitro on (Space / tap). */
  const nitroActiveRef = useRef(false);
  const slowUntilRef = useRef(0);
  const crashSmokeUntilRef = useRef(0);
  const lastFrameTsRef = useRef(0);
  const lastHudTsRef = useRef(0);
  const quizOpenRef = useRef(false);
  const endScreenRef = useRef(null);
  const gameStartedRef = useRef(false);
  const finishStageRef = useRef(async () => {});
  const activateNitroRef = useRef(() => {});
  const syncHudRef = useRef(() => {});
  const syncNitroHudRef = useRef(() => {});
  /** Combined -1..1 steer from touch / tilt (keys still win when held). */
  const steerAxisRef = useRef(0);
  const touchSteerRef = useRef(0);
  const tiltSteerRef = useRef(0);
  const tiltTargetRef = useRef(0);
  const touchStartRef = useRef(null);
  const tiltEnabledRef = useRef(false);
  const pausedAtRef = useRef(null);
  const pausedTotalRef = useRef(0);
  const helpOpenRef = useRef(false);
  const demoRedirectTriggeredRef = useRef(false);
  /** Quiz mode: all questions done — race until finish line is crossed. */
  const finishArmedRef = useRef(false);
  const finishCrossedRef = useRef(false);
  const mobileControlRef = useRef(readMobileControlMode());
  const isMobilePortraitRef = useRef(false);

  const [tiltEnabled, setTiltEnabled] = useState(false);
  const [tiltSupported, setTiltSupported] = useState(false);
  const [isMobilePortrait, setIsMobilePortrait] = useState(false);
  const [countdownLabel, setCountdownLabel] = useState(null);
  const [mobileControl, setMobileControl] = useState(() =>
    readMobileControlMode()
  );

  const isEndless = isEndlessMode(gameMode);
  const endlessMaxHits = useMemo(() => getEndlessMaxHits(themeData), [themeData]);
  const maxLifelinePlays = useMemo(
    () => getEndlessLifelinePlays(themeData),
    [themeData]
  );
  const [playsUsed, setPlaysUsed] = useState(0);
  const showLivesHud = isEndless && maxLifelinePlays > 0;
  const howToPlayRules = useMemo(
    () => getRaceHowToPlayRules(themeData, isEndless),
    [themeData, isEndless]
  );

  useEffect(() => {
    dispatch(setBackButtonUrl("/selection"));
  }, [dispatch]);

  useEffect(() => {
    maxPlaysRef.current = maxLifelinePlays;
  }, [maxLifelinePlays]);

  useEffect(() => {
    quizOpenRef.current = quizOpen;
  }, [quizOpen]);
  useEffect(() => {
    endScreenRef.current = endScreen;
  }, [endScreen]);
  useEffect(() => {
    gameStartedRef.current = gameStarted;
  }, [gameStarted]);

  useEffect(() => {
    const racing = gameStarted && !endScreen;
    if (!racing) {
      allowScreenSleep();
      return undefined;
    }
    keepScreenAwake();
    return () => allowScreenSleep();
  }, [gameStarted, endScreen]);
  useEffect(() => {
    tiltEnabledRef.current = tiltEnabled;
  }, [tiltEnabled]);
  useEffect(() => {
    mobileControlRef.current = mobileControl;
  }, [mobileControl]);
  useEffect(() => {
    isMobilePortraitRef.current = isMobilePortrait;
  }, [isMobilePortrait]);

  const syncSteerAxis = useCallback(() => {
    const mode = mobileControlRef.current;
    const touch = touchSteerRef.current;
    // Touch / on-screen buttons steer in swipe + arrows modes.
    if (
      Math.abs(touch) > 0.02 &&
      (!isMobilePortraitRef.current ||
        mode === MOBILE_CONTROL_SWIPE ||
        mode === MOBILE_CONTROL_ARROWS)
    ) {
      steerAxisRef.current = touch;
      return;
    }
    steerAxisRef.current =
      tiltEnabledRef.current && mode === MOBILE_CONTROL_MOTION
        ? tiltSteerRef.current
        : 0;
  }, []);

  useEffect(() => {
    const coarse =
      typeof window !== "undefined" &&
      window.matchMedia?.("(hover: none) and (pointer: coarse)").matches;
    const hasOrientation =
      typeof window !== "undefined" && "DeviceOrientationEvent" in window;
    setTiltSupported(Boolean(coarse && hasOrientation));

    const updatePortrait = () => {
      const portrait =
        typeof window !== "undefined" &&
        (window.matchMedia?.("(orientation: portrait) and (max-width: 900px)")
          .matches ||
          (coarse &&
            window.matchMedia?.("(orientation: portrait)").matches));
      setIsMobilePortrait(Boolean(portrait || (coarse && window.innerWidth < 900)));
    };
    updatePortrait();
    const mq = window.matchMedia?.("(orientation: portrait)");
    mq?.addEventListener?.("change", updatePortrait);
    window.addEventListener("resize", updatePortrait);
    return () => {
      mq?.removeEventListener?.("change", updatePortrait);
      window.removeEventListener("resize", updatePortrait);
    };
  }, []);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = useMemo(() => {
    const role = String(storedUser.role || "").toLowerCase();
    const source = String(storedUser.source || "").toUpperCase();
    const sessionId = String(storedUser.sessionId || "");
    return (
      sessionId === "demobypass" ||
      role === "demobypass" ||
      source === "DEMO" ||
      sessionId.startsWith("demo")
    );
  }, [storedUser]);
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(
    /\/+$/,
    ""
  );

  const raceAssets = useMemo(
    () => parseRaceAssetsFromTheme(themeData),
    [themeData]
  );

  useEffect(() => {
    setEngineSoundSrc(raceAssets.raceSoundUrl || "");
  }, [raceAssets.raceSoundUrl]);

  const selectedCarUrl = useMemo(() => {
    const file = readSessionValue(SELECTED_CAR_STORAGE_KEY);
    if (file) return getDvSourceImageUrl(file);
    return "";
  }, []);
  const raceSpeed = useMemo(() => {
    const difficulty = readSessionValue(DIFFICULTY_STORAGE_KEY) || "medium";
    const mode = readSessionValue(GAME_MODE_STORAGE_KEY);
    if (isEndlessMode(mode)) {
      return getEndlessDifficultyProfile(difficulty).speed;
    }
    return DIFFICULTY_SPEED[difficulty] || RACE_DEFAULT_SPEED;
  }, []);
  const quizList = useMemo(() => parseQuizFromTheme(themeData), [themeData]);
  const questionOnObstacle = useMemo(
    () => getQuestionOnObstacle(themeData),
    [themeData]
  );
  const questionOnPoints = useMemo(
    () => getQuestionOnPoints(themeData),
    [themeData]
  );
  const quizPoints = useMemo(() => getQuizPoints(themeData), [themeData]);
  const obstaclePoints = useMemo(() => getObstaclePoints(themeData), [themeData]);
  const distanceMultiplier = useMemo(
    () => getDistanceMultiplier(themeData),
    [themeData]
  );
  const gameTextColor = useMemo(
    () => getLandingPageTitleColor(themeData),
    [themeData]
  );
  const playButtonStyle = useMemo(() => {
    const { start, end } = getThemeButtonGradient(themeData);
    const text = getThemeButtonTextColor(themeData);
    if (!start && !text) return undefined;
    const shadow = start
      ? darkenThemeHexColor(start, 0.35) || start
      : null;
    return {
      ...(start
        ? {
            "--race-play-btn-start": start,
            "--race-play-btn-end": end || start,
            "--race-play-btn-shadow": shadow || start,
          }
        : null),
      ...(text ? { "--race-play-btn-text": text } : null),
    };
  }, [themeData]);

  useEffect(() => {
    assetsRef.current = raceAssets;
    questionOnObstacleRef.current = questionOnObstacle;
    questionOnPointsRef.current = questionOnPoints;
    quizPointsRef.current = quizPoints;
    obstaclePointsRef.current = obstaclePoints;
    distanceMultiplierRef.current = distanceMultiplier;
    quizDeckRef.current = shuffle(quizList);
    raceSpeedRef.current = raceSpeed;
    const difficulty = readSessionValue(DIFFICULTY_STORAGE_KEY) || "medium";
    const profile = getEndlessDifficultyProfile(difficulty);
    endlessDiffRef.current = profile;
    activeEndlessProfile = profile;
    selectedCarUrlRef.current = selectedCarUrl;
  }, [
    raceAssets,
    questionOnObstacle,
    questionOnPoints,
    quizPoints,
    obstaclePoints,
    distanceMultiplier,
    quizList,
    raceSpeed,
    selectedCarUrl,
  ]);

  useEffect(() => {
    if (themeStatus === "idle") {
      dispatch(fetchThemeData({ themeId: null }));
    }
  }, [dispatch, themeStatus]);

  useEffect(() => {
    pointsRef.current = stage1Points;
    if (isEndless) {
      setDisplayTotalScore(stage1Points);
    } else {
      setDisplayTotalScore(otherStagesTotalRef.current + stage1Points);
    }
  }, [stage1Points, isEndless]);

  useEffect(() => {
    bestScoreRef.current = bestScore;
  }, [bestScore]);

  const stopLoop = useCallback(() => {
    playerRef.current.start = false;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    stopEngineSound();
  }, []);

  const redirectDemoToPlans = useCallback(() => {
    if (demoRedirectTriggeredRef.current) return;
    demoRedirectTriggeredRef.current = true;
    stage1CompleteRef.current = true;
    playerRef.current.start = false;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    stopAllRaceSounds();
    void Swal.fire({
      icon: "info",
      text: "Thank you for playing. Subscribe to any PLAN to play with your peers.",
      confirmButtonText: "OK",
      allowOutsideClick: false,
      allowEscapeKey: false,
    }).then(() => {
      window.location.assign(DEMO_PLANS_URL);
    });
  }, []);

  const pauseRaceForHelp = useCallback(() => {
    helpOpenRef.current = true;
    if (!pausedAtRef.current) pausedAtRef.current = Date.now();
    playerRef.current.start = false;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    }
    updateEngineSound({ muted: true });
  }, []);

  const resumeRaceAfterHelp = useCallback(() => {
    if (!helpOpenRef.current && !pausedAtRef.current) return;
    helpOpenRef.current = false;
    if (pausedAtRef.current) {
      pausedTotalRef.current += Date.now() - pausedAtRef.current;
      pausedAtRef.current = null;
    }
    if (
      !gameStartedRef.current ||
      stage1CompleteRef.current ||
      submittedRef.current ||
      quizOpenRef.current ||
      endScreenRef.current ||
      raceAlertResolverRef.current
    ) {
      return;
    }
    playerRef.current.start = true;
    lastFrameTsRef.current = 0;
    startEngineSound();
    if (!rafRef.current) {
      rafRef.current = requestAnimationFrame(gamePlayRef.current);
    }
  }, []);

  // Race clock is wall-clock based, but the game loop (rAF) stops while the tab
  // is hidden / phone locked. Exclude hidden time so a backgrounded run doesn't
  // record hours of "play" (e.g. 288:37 for a few minutes of racing).
  useEffect(() => {
    let hiddenAt = null;
    const onVisibility = () => {
      if (document.hidden) {
        if (sessionStartRef.current && !stage1CompleteRef.current) {
          hiddenAt = Date.now();
        }
        return;
      }
      if (hiddenAt == null) return;
      const gap = Date.now() - hiddenAt;
      hiddenAt = null;
      // A help/pause already open while hidden is counted by pausedAtRef itself.
      if (pausedAtRef.current) return;
      if (sessionStartRef.current && !stage1CompleteRef.current) {
        pausedTotalRef.current += gap;
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  const syncHud = useCallback(() => {
    const endless = isEndlessMode(gameModeRef.current);
    const score = Math.max(0, Math.floor(pointsRef.current));
    const display = endless
      ? score
      : otherStagesTotalRef.current + score;
    if (hudScoreRef.current) {
      hudScoreRef.current.textContent = String(display);
    }
    if (hudTimeRef.current) {
      hudTimeRef.current.textContent = formatSecondsToClock(elapsedRef.current);
    }
    if (hudBestRef.current) {
      hudBestRef.current.textContent = String(
        Math.max(0, Math.floor(bestScoreRef.current))
      );
    }
    if (hudDistRef.current) {
      hudDistRef.current.textContent = `${Math.floor(distanceRef.current)}m`;
    }
    if (hudLivesRef.current) {
      const maxPlays = maxPlaysRef.current || 0;
      const left = Math.max(0, maxPlays - playsUsedRef.current);
      hudLivesRef.current.textContent = String(left);
    }
    if (hudSpeedRef.current) {
      const spd = Math.max(
        0,
        Math.round((playerRef.current.speed || 0) * 14)
      );
      hudSpeedRef.current.textContent = String(spd);
    }

    syncNitroHudRef.current();
  }, []);

  const syncNitroHud = useCallback(() => {
    const charge = Math.max(
      0,
      Math.min(NITRO_MAX, nitroChargeRef.current)
    );
    nitroChargeRef.current = charge;
    const nitroOn = nitroActiveRef.current && charge > 0;
    if (nitroRootRef.current) {
      nitroRootRef.current.classList.toggle("is-active", nitroOn);
      nitroRootRef.current.classList.toggle("is-ready", !nitroOn && charge >= 99.5);
      nitroRootRef.current.classList.toggle("is-hot", nitroOn && charge > 60);
      nitroRootRef.current.classList.toggle(
        "is-warm",
        nitroOn && charge > 25 && charge <= 60
      );
      nitroRootRef.current.classList.toggle("is-low", charge > 0 && charge <= 25);
      nitroRootRef.current.style.setProperty("--nitro-pct", `${charge}%`);
    }
    if (nitroFillRef.current) {
      nitroFillRef.current.style.setProperty("--nitro-pct", `${charge}%`);
    }
    if (nitroLabelRef.current) {
      nitroLabelRef.current.textContent = `NITRO ${Math.ceil(charge)}%`;
    }
  }, []);

  syncHudRef.current = syncHud;
  syncNitroHudRef.current = syncNitroHud;

  const syncCarFx = useCallback(() => {
    const track = trackRef.current;
    const car = track?.querySelector(".race-stage__car");
    if (!car) return;
    const now = Date.now();
    const nitroOn =
      nitroActiveRef.current && nitroChargeRef.current > 0;
    car.classList.toggle("is-nitro", nitroOn);
    car.classList.toggle(
      "is-smoke",
      now < slowUntilRef.current || now < crashSmokeUntilRef.current
    );
  }, []);

  /** Space / tap: toggle nitro on or off. */
  const activateNitro = useCallback(() => {
    if (stage1CompleteRef.current || submittedRef.current) return;
    if (!raceStartRef.current) return;
    if (helpOpenRef.current || quizOpenRef.current || endScreenRef.current) {
      return;
    }
    if (nitroActiveRef.current) {
      nitroActiveRef.current = false;
      syncCarFx();
      syncNitroHud();
      return;
    }
    if (nitroChargeRef.current <= 0) {
      syncNitroHud();
      return;
    }
    nitroActiveRef.current = true;
    playBoostSound();
    startEngineSound();
    syncCarFx();
    syncNitroHud();
  }, [syncCarFx, syncNitroHud]);

  /** Quiz rewards: turn nitro on (do not toggle off). */
  const engageNitro = useCallback(() => {
    if (stage1CompleteRef.current || submittedRef.current) return;
    if (!raceStartRef.current) return;
    nitroChargeRef.current = Math.min(
      NITRO_MAX,
      Math.max(nitroChargeRef.current, 35) + 20
    );
    if (nitroChargeRef.current > 0) {
      nitroActiveRef.current = true;
      playBoostSound();
      startEngineSound();
    }
    syncCarFx();
    syncNitroHud();
  }, [syncCarFx, syncNitroHud]);

  activateNitroRef.current = activateNitro;

  const finishStage = useCallback(
    async (skip = false, options = {}) => {
      if (submitting || submittedRef.current || stage1CompleteRef.current) return;
      submittedRef.current = true;
      stage1CompleteRef.current = true;
      setSubmitting(true);
      stopLoop();
      setQuizOpen(false);
      setRaceAlert(null);
      raceAlertResolverRef.current = null;
      nitroActiveRef.current = false;
      stopAllRaceSounds();
      syncHud();

      const mode =
        readSessionValue(GAME_MODE_STORAGE_KEY) ||
        gameModeRef.current ||
        GAME_MODE_QUIZ;
      gameModeRef.current = mode;
      setGameMode(mode);
      const endless = isEndlessMode(mode);

      let stage1Score = skip ? 0 : Math.max(0, Math.floor(pointsRef.current));
      if (endless && !skip) {
        stage1Score = computeEndlessScore({
          obstaclesAvoided: obstaclesAvoidedRef.current,
          distanceTravelled: distanceRef.current,
          pointsPerObstacle: obstaclePointsRef.current,
          distanceMultiplier: distanceMultiplierRef.current,
        });
        pointsRef.current = stage1Score;
        setStage1Points(stage1Score);
      } else if (!skip) {
        setStage1Points(stage1Score);
      }

      const finalTotal = endless
        ? stage1Score
        : otherStagesTotalRef.current + stage1Score;

      if (sessionStartRef.current) {
        const pauseHold = pausedAtRef.current
          ? Date.now() - pausedAtRef.current
          : 0;
        const live = Math.max(
          0,
          Math.floor(
            (Date.now() -
              sessionStartRef.current -
              pausedTotalRef.current -
              pauseHold) /
              1000
          )
        );
        elapsedRef.current = baseElapsedRef.current + live;
        setElapsedSec(elapsedRef.current);
      }
      const finalTime = formatSecondsToClock(elapsedRef.current);
      const priorBest = Math.max(0, Number(bestScoreRef.current) || 0);
      const nextBest = Math.max(priorBest, stage1Score);
      if (stage1Score > priorBest) {
        setBestScore(stage1Score);
        bestScoreRef.current = stage1Score;
      }

      let nextPlaysUsed = playsUsedRef.current;
      const maxPlays = endless ? getEndlessLifelinePlays(themeData) : 0;
      if (endless && maxPlays > 0) {
        nextPlaysUsed = playsUsedRef.current + 1;
        playsUsedRef.current = nextPlaysUsed;
        setPlaysUsed(nextPlaysUsed);
      }
      const canReplay =
        !endless || maxPlays === 0 || nextPlaysUsed < maxPlays;

      // Combined total (quiz + endless best) as stored in DB / external report.
      let combinedTotal = finalTotal;
      try {
        if (!isDemoBypass && backendBase && adminToken) {
          const saveResult = await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId: stageRowId,
            // Separate columns per mode; server sums them into total_score.
            stageNumber: endless ? ENDLESS_SCORE_STAGE : QUIZ_SCORE_STAGE,
            score: stage1Score,
            time: finalTime,
            wrongcount: hitCountRef.current,
            gameover: !endless,
            mode,
            user_answer: endless ? undefined : userAnswersRef.current,
            onlyIfHigher: endless,
            currentQues: endless && maxPlays > 0 ? nextPlaysUsed : undefined,
          });
          if (saveResult?.stageId) {
            setStageRowId(String(saveResult.stageId));
            sessionStorage.setItem(STAGE_ROW_KEY, String(saveResult.stageId));
          }
          if (saveResult?.total_score != null) {
            combinedTotal = Number(saveResult.total_score) || 0;
          }
          if (saveResult?.stageScore != null && endless) {
            const kept = Math.max(Number(saveResult.stageScore) || 0, nextBest);
            setBestScore(kept);
            bestScoreRef.current = kept;
          }
        }
      } catch (err) {
        console.error("Stage1 finish:", err);
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);

      if (isDemoBypass) {
        redirectDemoToPlans();
        return;
      }

      if (endless) {
        // Endless can play again from the end screen / leaderboard.
        setEndScreen({
          score: stage1Score,
          bestScore: Math.max(nextBest, bestScoreRef.current),
          distance: Math.floor(distanceRef.current),
          survivalSec: elapsedRef.current,
          reason: options.reason || "crash",
          canReplay,
        });
        setSubmitting(false);
        return;
      }

      // Quiz mode: gameover → feedback, then leaderboard
      await flushLayoutFrame();
      navigate("/thankyou", {
        replace: true,
        state: {
          points: combinedTotal,
          time: finalTime,
        },
      });
    },
    [
      submitting,
      stopLoop,
      isDemoBypass,
      backendBase,
      adminToken,
      storedUser,
      stageRowId,
      navigate,
      syncHud,
      redirectDemoToPlans,
      themeData,
    ]
  );

  finishStageRef.current = finishStage;

  const openQuiz = useCallback(() => {
    if (isEndlessMode(gameModeRef.current)) return;
    if (finishArmedRef.current || finishCrossedRef.current) return;
    const deck = quizDeckRef.current;
    if (!deck.length) return;
    if (questionsAnsweredRef.current >= deck.length) return;
    const idx = questionIndexRef.current % deck.length;
    const q = deck[idx];
    playerRef.current.start = false;
    stopEngineSound();
    setSelectedAnswer("");
    setQuizQuestion(q);
    setQuizOpen(true);
  }, []);

  const resumeRace = useCallback(() => {
    if (stage1CompleteRef.current || submittedRef.current) return;
    if (helpOpenRef.current) return;
    setRaceAlert(null);
    setQuizOpen(false);
    playerRef.current.start = true;
    lastFrameTsRef.current = 0;
    startEngineSound();
    rafRef.current = requestAnimationFrame(gamePlayRef.current);
  }, []);

  const closeRaceAlert = useCallback(() => {
    const resolve = raceAlertResolverRef.current;
    raceAlertResolverRef.current = null;
    setRaceAlert(null);
    resolve?.();
  }, []);

  const openRaceAlert = useCallback((alert) => {
    return new Promise((resolve) => {
      raceAlertResolverRef.current = resolve;
      setRaceAlert(alert);
    });
  }, []);

  useEffect(() => {
    if (!raceAlert?.autoMs) return undefined;
    const t = window.setTimeout(() => closeRaceAlert(), raceAlert.autoMs);
    return () => window.clearTimeout(t);
  }, [raceAlert, closeRaceAlert]);

  const gamePlayRef = useRef(() => {});

  gamePlayRef.current = () => {
    const track = trackRef.current;
    const car = track?.querySelector(".race-stage__car");
    if (
      !track ||
      !car ||
      !playerRef.current.start ||
      helpOpenRef.current
    ) {
      return;
    }

    const now = Date.now();
    const lastTs = lastFrameTsRef.current || now;
    const dt = Math.min(0.05, Math.max(0, (now - lastTs) / 1000));
    lastFrameTsRef.current = now;

    const road = track.getBoundingClientRect();
    const carWidth = car.offsetWidth;
    const carHeight = car.offsetHeight;
    const player = playerRef.current;
    const keys = keysRef.current;
    const endless = isEndlessMode(gameModeRef.current);

    let baseSpeed = raceSpeedRef.current || RACE_DEFAULT_SPEED;
    const diffProfile = endlessDiffRef.current || getEndlessDifficultyProfile("medium");
    if (endless && raceStartRef.current) {
      const runSec = Math.max(0, (now - raceStartRef.current) / 1000);
      const ramp = Math.min(
        diffProfile.maxBonus ?? ENDLESS_MAX_SPEED_BONUS,
        runSec * (diffProfile.rampPerSec ?? ENDLESS_SPEED_RAMP_PER_SEC)
      );
      baseSpeed += ramp;
    }

    const nitroOn =
      nitroActiveRef.current && nitroChargeRef.current > 0;
    const slowOn = !endless && now < slowUntilRef.current;
    let speedMul = 1;
    if (nitroOn) {
      speedMul = endless
        ? diffProfile.nitroMul ?? NITRO_SPEED_MULTIPLIER
        : NITRO_SPEED_MULTIPLIER;
    } else if (slowOn) speedMul = QUIZ_WRONG_SLOW_FACTOR;
    player.speed = baseSpeed * speedMul;

    // Live nitro meter: drain while on, slow regen while off.
    if (nitroOn) {
      nitroChargeRef.current = Math.max(
        0,
        nitroChargeRef.current - NITRO_DRAIN_PER_SEC * dt
      );
      if (nitroChargeRef.current <= 0) {
        nitroChargeRef.current = 0;
        nitroActiveRef.current = false;
      }
    } else if (raceStartRef.current && player.start) {
      nitroChargeRef.current = Math.min(
        NITRO_MAX,
        nitroChargeRef.current + NITRO_REGEN_PER_SEC * dt
      );
    }
    syncNitroHudRef.current();

    car.classList.toggle(
      "is-nitro",
      nitroActiveRef.current && nitroChargeRef.current > 0
    );
    car.classList.toggle(
      "is-smoke",
      slowOn || now < crashSmokeUntilRef.current
    );

    if (endless) {
      distanceRef.current += player.speed * dt * 2.2;
      pointsRef.current = computeEndlessScore({
        obstaclesAvoided: obstaclesAvoidedRef.current,
        distanceTravelled: distanceRef.current,
        pointsPerObstacle: obstaclePointsRef.current,
        distanceMultiplier: distanceMultiplierRef.current,
      });
    }

    if (now - lastHudTsRef.current >= 100) {
      lastHudTsRef.current = now;
      syncHudRef.current();
    }

    const lines = track.querySelectorAll(".race-stage__line");
    lines.forEach((item) => {
      if (item.y >= 700) item.y -= 750;
      item.y += player.speed;
      item.style.top = `${item.y}px`;
    });

    const spawnGap = (() => {
      if (!endless || !raceStartRef.current) return ENDLESS_SPAWN_BASE;
      const runSec = Math.max(0, (now - raceStartRef.current) / 1000);
      return Math.max(
        diffProfile.spawnMin ?? ENDLESS_SPAWN_MIN,
        (diffProfile.spawnBase ?? ENDLESS_SPAWN_BASE) -
          runSec * (diffProfile.spawnRamp ?? ENDLESS_SPAWN_RAMP_PER_SEC)
      );
    })();

    const approachStepCap = maxApproachStep(track, player.speed, diffProfile);
    const obstacles = track.querySelectorAll(".race-stage__obstacle");
    for (let i = 0; i < obstacles.length; i += 1) {
      const item = obstacles[i];

      if (item.y >= track.clientHeight) {
        respawnObstacle(
          item,
          track,
          obstacles,
          spawnGap,
          player.speed,
          clampLane(player.targetLane ?? player.lane ?? 1)
        );

        obstaclesAvoidedRef.current += 1;
        nitroChargeRef.current = Math.min(
          NITRO_MAX,
          nitroChargeRef.current + NITRO_GAIN_ON_OBSTACLE
        );

        if (!endless) {
          pointsRef.current += obstaclePointsRef.current;
          pointsForQuestionRef.current += obstaclePointsRef.current;
          obstaclesForQuestionRef.current += 1;
          syncHudRef.current();

          const quizDone =
            finishArmedRef.current ||
            (quizDeckRef.current.length > 0 &&
              questionsAnsweredRef.current >= quizDeckRef.current.length);

          if (!quizDone) {
            const hitObstacleGate =
              obstaclesForQuestionRef.current >= questionOnObstacleRef.current;
            const hitPointsGate =
              questionOnPointsRef.current > 0 &&
              pointsForQuestionRef.current >= questionOnPointsRef.current;

            if (hitObstacleGate || hitPointsGate) {
              obstaclesForQuestionRef.current = 0;
              pointsForQuestionRef.current = 0;
              stopEngineSound();
              openQuiz();
              return;
            }
          }
        }
      }

      // Off-screen traffic travels freely; once it is about to enter the view
      // its closing speed is capped so it can never dive onto the car.
      const rawStep = player.speed * (item.speedFactor || 1);
      item.y +=
        item.y > -OBSTACLE_ENTER_MARGIN
          ? Math.min(rawStep, approachStepCap)
          : rawStep;
      item.style.top = `${item.y}px`;
    }

    const avoidLane = clampLane(player.targetLane ?? player.lane ?? 1);
    separateOverlappingObstacles(obstacles, track, avoidLane, player.y);
    ensurePassableCorridor(obstacles, track, avoidLane, player.y);

    // Quiz final stretch: scroll finish line down until the car crosses it.
    if (!endless && finishArmedRef.current && !finishCrossedRef.current) {
      const finish = track.querySelector(".race-stage__finish");
      if (finish) {
        finish.y = (finish.y || 0) + player.speed;
        finish.style.top = `${finish.y}px`;
        const finishH = finish.offsetHeight || 56;
        const finishBottom = (finish.y || 0) + finishH;
        if (finishBottom >= player.y + carHeight * 0.3) {
          finishCrossedRef.current = true;
          player.start = false;
          stopEngineSound();
          finishStageRef.current(false, { reason: "finish" });
          return;
        }
      }
    }

    // Player movement first, then solid resolve so you cannot drive through traffic.
    const keyUp = keys.ArrowUp || keys.w || keys.W;
    const keyDown = keys.ArrowDown || keys.s || keys.S;
    if (keyUp && player.y > 40) player.y -= player.speed;
    if (keyDown && player.y < road.height - carHeight - 20) {
      player.y += player.speed;
    }

    // Motion: ease steer each frame so sparse orientation events still feel smooth.
    if (
      tiltEnabledRef.current &&
      mobileControlRef.current === MOBILE_CONTROL_MOTION &&
      isMobilePortraitRef.current
    ) {
      const target = tiltTargetRef.current;
      const cur = tiltSteerRef.current;
      tiltSteerRef.current = cur + (target - cur) * TILT_SMOOTH;
      if (Math.abs(tiltSteerRef.current) < 0.015 && Math.abs(target) < 0.02) {
        tiltSteerRef.current = 0;
      }
      syncSteerAxis();
    }

    const keyLeft = keys.ArrowLeft || keys.a || keys.A;
    const keyRight = keys.ArrowRight || keys.d || keys.D;
    let steer = 0;
    if (keyLeft) steer -= 1;
    if (keyRight) steer += 1;
    if (!keyLeft && !keyRight) {
      steer = Math.max(-1, Math.min(1, steerAxisRef.current || 0));
    }

    const swipeLanes =
      isMobilePortraitRef.current &&
      mobileControlRef.current === MOBILE_CONTROL_SWIPE;

    if (swipeLanes) {
      const settled = clampLane(player.lane ?? 1);
      const dest = clampLane(player.targetLane ?? settled);
      const targetX = laneLeft(track, dest, carWidth);
      const steerLerp = endless
        ? diffProfile.steerLerp ?? LANE_SNAP_LERP
        : LANE_SNAP_LERP;
      player.x += (targetX - player.x) * steerLerp;
      if (Math.abs(targetX - player.x) < LANE_ARRIVE_PX) {
        player.x = targetX;
        player.lane = dest;
        player.targetLane = dest;
      }
      player.x = Math.max(0, Math.min(road.width - carWidth, player.x));
    } else {
      const motionBoost =
        isMobilePortraitRef.current &&
        mobileControlRef.current === MOBILE_CONTROL_MOTION
          ? TILT_STEER_MULT
          : 1;
      const analogMul = endless
        ? diffProfile.steerMul ?? 1
        : 1;
      if (steer < 0 && player.x > 0) {
        player.x = Math.max(
          0,
          player.x + player.speed * motionBoost * analogMul * steer
        );
      } else if (steer > 0 && player.x < road.width - carWidth) {
        player.x = Math.min(
          road.width - carWidth,
          player.x + player.speed * motionBoost * analogMul * steer
        );
      }
    }

    const playerLane = clampLane(player.targetLane ?? player.lane ?? 1);
    const viewH = track.clientHeight || 700;
    for (let i = 0; i < obstacles.length; i += 1) {
      const item = obstacles[i];
      const lane = inferLane(item, track);
      const prevLane = item.prevLane;
      const tooCloseInPlayerLane =
        lane === playerLane && (item.y || 0) > -viewH * 0.95;
      const jumpedIntoPlayerLane =
        prevLane != null &&
        prevLane !== lane &&
        lane === playerLane &&
        (item.y || 0) > -viewH * 1.05;
      const spawnedOnTop =
        prevLane == null && tooCloseInPlayerLane;
      if (jumpedIntoPlayerLane || spawnedOnTop) {
        const kickY = -minSpawnLeadY(track, player.speed, spawnGap);
        item.y = kickY;
        item.style.top = `${kickY}px`;
        const safeLane =
          prevLane != null && prevLane !== playerLane
            ? prevLane
            : (playerLane + 1) % OBSTACLE_LANES;
        item.lane = safeLane;
        const w = item.offsetWidth || OBSTACLE_WIDTH_FALLBACK;
        item.x = laneLeft(track, safeLane, w);
        item.style.left = `${item.x}px`;
        item.prevLane = safeLane;
      } else {
        item.prevLane = lane;
      }
    }

    let hitThisFrame = false;
    for (let i = 0; i < obstacles.length; i += 1) {
      const item = obstacles[i];
      const overlapping = resolvePlayerObstacleSolid(player, car, item, road);
      if (!overlapping) continue;

      if (!item.collided) {
        item.collided = true;
        hitThisFrame = true;
        hitCountRef.current += 1;
        syncHudRef.current();
        playCrashSound();
        crashSmokeUntilRef.current = now + CRASH_SMOKE_MS;
        nitroActiveRef.current = false;
        car.classList.remove("is-nitro");
        car.classList.add("is-smoke");
        window.setTimeout(() => syncCarFx(), CRASH_SMOKE_MS);
        track.classList.add("is-crash");
        window.setTimeout(() => track.classList.remove("is-crash"), 400);
      }
    }

    car.style.top = `${player.y}px`;
    car.style.left = `${player.x}px`;

    updateEngineSound({
      speed: player.speed,
      nitro: nitroOn,
      muted: false,
    });

    if (hitThisFrame && endless) {
      const maxHits = endlessMaxHitsRef.current || 1;
      if (hitCountRef.current >= maxHits) {
        player.start = false;
        stopEngineSound();
        car.style.top = `${player.y}px`;
        car.style.left = `${player.x}px`;
        window.setTimeout(() => {
          finishStageRef.current(false, { reason: "crash" });
        }, CRASH_END_DELAY_MS);
        return;
      }
    }

    if (!player.start || helpOpenRef.current) return;
    rafRef.current = requestAnimationFrame(gamePlayRef.current);
  };

  const startRace = useCallback(async () => {
    const track = trackRef.current;
    if (!track || stage1CompleteRef.current) return;

    const mode = readGameMode();
    gameModeRef.current = mode;
    setGameMode(mode);

    const difficulty = readSessionValue(DIFFICULTY_STORAGE_KEY) || "medium";
    const profile = getEndlessDifficultyProfile(difficulty);
    endlessDiffRef.current = profile;
    activeEndlessProfile = profile;
    if (isEndlessMode(mode)) {
      raceSpeedRef.current = profile.speed;
    }

    if (
      isEndlessMode(mode) &&
      maxPlaysRef.current > 0 &&
      playsUsedRef.current >= maxPlaysRef.current
    ) {
      return;
    }

    const validation = validateThemeStageData(themeData, { mode });
    if (!validation.ok) {
      setThemeDataBlocked(true);
      await fireThemeStageDataMissingSwal(validation.missing);
      return;
    }

    const { roadUrl, carUrls, obstacleUrls, raceSoundUrl } =
      parseRaceAssetsFromTheme(themeData);
    assetsRef.current = { ...assetsRef.current, roadUrl, carUrls, obstacleUrls };
    if (!roadUrl || !carUrls.length || !obstacleUrls.length) {
      setThemeDataBlocked(true);
      await fireThemeStageDataMissingSwal([
        !roadUrl ? "Stage 1 (Race): upload a road image" : null,
        !carUrls.length
          ? "Stage 1 (Race): upload at least 1 car image (PNG/GIF)"
          : null,
        !obstacleUrls.length
          ? "Stage 1 (Race): upload at least 1 obstacle image"
          : null,
      ].filter(Boolean));
      return;
    }

    if (!isEndlessMode(mode)) {
      const quiz = parseQuizFromTheme(themeData);
      if (!quiz.length) {
        setThemeDataBlocked(true);
        await fireThemeStageDataMissingSwal([
          "Stage 1 (Race): need at least 1 quiz question (found 0)",
        ]);
        return;
      }
      quizDeckRef.current = shuffle(quiz);
    }

    track.innerHTML = "";
    track.classList.remove("is-crash");
    hitCountRef.current = 0;
    endlessMaxHitsRef.current = getEndlessMaxHits(themeData);
    obstaclesForQuestionRef.current = 0;
    pointsForQuestionRef.current = 0;
    obstaclesAvoidedRef.current = 0;
    distanceRef.current = 0;
    userAnswersRef.current = [];
    nitroChargeRef.current = NITRO_START;
    nitroActiveRef.current = false;
    slowUntilRef.current = 0;
    crashSmokeUntilRef.current = 0;
    touchSteerRef.current = 0;
    tiltSteerRef.current = 0;
    tiltTargetRef.current = 0;
    steerAxisRef.current = 0;
    touchStartRef.current = null;
    pausedAtRef.current = null;
    pausedTotalRef.current = 0;
    helpOpenRef.current = false;
    finishArmedRef.current = false;
    finishCrossedRef.current = false;
    stopAllRaceSounds();
    setEndScreen(null);
    setRaceAlert(null);
    setQuizOpen(false);
    setCountdownLabel(null);
    lastFrameTsRef.current = 0;
    lastHudTsRef.current = 0;
    raceStartRef.current = null;

    if (isEndlessMode(mode)) {
      pointsRef.current = 0;
      setStage1Points(0);
      baseElapsedRef.current = 0;
      elapsedRef.current = 0;
      setElapsedSec(0);
      sessionStartRef.current = null;
      timerStartedRef.current = false;
    }

    // Keep ref in sync immediately — state update alone used to remount key listeners and kill the loop.
    gameStartedRef.current = true;
    keepScreenAwake();
    setGameStarted(true);

    for (let col = 1; col < OBSTACLE_LANES; col += 1) {
      const pct = (col / OBSTACLE_LANES) * 100;
      for (let x = 0; x < 5; x += 1) {
        const line = document.createElement("div");
        line.className = "race-stage__line";
        line.style.left = `${pct}%`;
        line.y = x * 150;
        line.style.top = `${line.y}px`;
        track.appendChild(line);
      }
    }

    const car = document.createElement("div");
    car.className = "race-stage__car is-player";
    const carUrl =
      selectedCarUrlRef.current && carUrls.includes(selectedCarUrlRef.current)
        ? selectedCarUrlRef.current
        : selectedCarUrlRef.current || pickRandom(carUrls);
    car.style.backgroundImage = `url("${carUrl}")`;
    // Position with top/left only — CSS bottom would fight vertical movement.
    car.style.bottom = "auto";
    car.style.marginLeft = "0";

    const flameFx = document.createElement("span");
    flameFx.className = "race-stage__fx race-stage__fx--flame";
    car.appendChild(flameFx);
    // Multiple smoke layers so crash smoke reads clearly on mobile.
    for (let s = 0; s < 3; s += 1) {
      const smokeFx = document.createElement("span");
      smokeFx.className = `race-stage__fx race-stage__fx--smoke race-stage__fx--smoke-${s}`;
      car.appendChild(smokeFx);
    }

    track.appendChild(car);

    const startLane = Math.floor(OBSTACLE_LANES / 2);
    const startCarW = 52;
    playerRef.current = {
      start: false,
      speed: raceSpeedRef.current || RACE_DEFAULT_SPEED,
      lane: startLane,
      targetLane: startLane,
      x: laneLeft(track, startLane, startCarW),
      y: Math.max(40, track.clientHeight - 190),
      score: 0,
    };
    car.style.left = `${playerRef.current.x}px`;
    car.style.top = `${playerRef.current.y}px`;
    syncHud();

    const spawnedObstacles = [];
    const spawnSpeed = raceSpeedRef.current || RACE_DEFAULT_SPEED;
    const diffProfile = endlessDiffRef.current || getEndlessDifficultyProfile("medium");
    const spawnBase = diffProfile.spawnBase ?? ENDLESS_SPAWN_BASE;
    const obstacleCount = Math.max(
      3,
      Number(diffProfile.obstacleCount) || RACE_OBSTACLE_COUNT
    );
    const lead = minSpawnLeadY(track, spawnSpeed, spawnBase);
    const stagger = Math.max(
      Math.round((diffProfile.spawnMin ?? 140) * 1.2),
      Math.round(
        (track.clientHeight || 700) * (obstacleCount >= 5 ? 0.24 : 0.42)
      )
    );
    const startAvoid = clampLane(startLane);
    for (let x = 0; x < obstacleCount; x += 1) {
      const obstacle = document.createElement("div");
      obstacle.className = "race-stage__obstacle";
      obstacle.style.backgroundImage = `url("${pickRandom(obstacleUrls)}")`;
      obstacle.y = clampSpawnOffscreen(track, -lead - x * stagger);
      obstacle.style.top = `${obstacle.y}px`;
      track.appendChild(obstacle);
      placeObstacle(obstacle, track, spawnedObstacles, startAvoid);
      randomizeObstacleMotion(obstacle);
      spawnedObstacles.push(obstacle);
    }

    setStageActive(STAGE_NUM, storedUser);
    setEngineSoundSrc(raceSoundUrl || "");
    await unlockRaceAudio();

    // Re-arm motion tilt if that control mode is selected (e.g. replay).
    if (
      isMobilePortraitRef.current &&
      mobileControlRef.current === MOBILE_CONTROL_MOTION &&
      !tiltEnabledRef.current
    ) {
      try {
        const DOE = window.DeviceOrientationEvent;
        if (DOE && typeof DOE.requestPermission === "function") {
          const result = await DOE.requestPermission();
          if (result === "granted") {
            setTiltEnabled(true);
            tiltEnabledRef.current = true;
          }
        } else {
          setTiltEnabled(true);
          tiltEnabledRef.current = true;
        }
      } catch {
        /* ignore */
      }
    }

    const sequence = ["3", "2", "1", "GO"];
    for (let i = 0; i < sequence.length; i += 1) {
      if (stage1CompleteRef.current || !gameStartedRef.current) return;
      const label = sequence[i];
      setCountdownLabel(label);
      playCountdownSound(label === "GO");
      await new Promise((resolve) => {
        window.setTimeout(resolve, label === "GO" ? 700 : 850);
      });
    }
    setCountdownLabel(null);

    if (stage1CompleteRef.current || !gameStartedRef.current) return;

    const now = Date.now();
    raceStartRef.current = now;
    if (isEndlessMode(mode)) {
      sessionStartRef.current = now;
      timerStartedRef.current = true;
    } else if (!timerStartedRef.current) {
      timerStartedRef.current = true;
      sessionStartRef.current = now;
    }

    playerRef.current.start = true;
    lastFrameTsRef.current = 0;
    startEngineSound();
    updateEngineSound({
      speed: raceSpeedRef.current || RACE_DEFAULT_SPEED,
      nitro: false,
      muted: false,
    });
    rafRef.current = requestAnimationFrame(gamePlayRef.current);
  }, [storedUser, syncHud, themeData]);

  const replayEndless = useCallback(() => {
    if (maxPlaysRef.current > 0 && playsUsedRef.current >= maxPlaysRef.current) {
      return;
    }
    submittedRef.current = false;
    stage1CompleteRef.current = false;
    setSubmitting(false);
    setEndScreen(null);
    questionIndexRef.current = 0;
    questionsAnsweredRef.current = 0;
    startRace();
  }, [startRace]);

  const handleQuizSubmit = useCallback(
    async (answerOverride) => {
      const answer =
        typeof answerOverride === "string" ? answerOverride : selectedAnswer;
      if (!quizQuestion || !answer || submitting) return;

      setSelectedAnswer(answer);
      setSubmitting(true);

      const correct =
        answer.trim().toLowerCase() ===
        String(quizQuestion.correct_answer || "")
          .trim()
          .toLowerCase();

      userAnswersRef.current = [
        ...userAnswersRef.current,
        {
          question_name: String(quizQuestion.question_name || ""),
          selected_answer: String(answer),
          correct_answer: String(quizQuestion.correct_answer || ""),
          is_correct: correct,
        },
      ];

      try {
        if (correct) {
          const award = getQuizPoints(themeData);
          quizPointsRef.current = award;
          pointsRef.current += award;
          setStage1Points(pointsRef.current);
          setQuizOpen(false);
          engageNitro();
          await openRaceAlert({
            variant: "correct",
            title: "Correct!",
            message: `+${award} point${award === 1 ? "" : "s"} · Nitro engaged!`,
            autoMs: 1100,
          });
        } else {
          setQuizOpen(false);
          slowUntilRef.current = Date.now() + QUIZ_WRONG_SLOW_MS;
          const track = trackRef.current;
          if (track) {
            track.classList.add("is-crash");
            window.setTimeout(() => track.classList.remove("is-crash"), 450);
          }
          syncCarFx();
          await openRaceAlert({
            variant: "wrong",
            title: "Wrong answer",
            message: `Correct answer: ${quizQuestion.correct_answer}`,
            confirmLabel: "Continue",
          });
        }

        questionIndexRef.current += 1;
        questionsAnsweredRef.current += 1;

        const totalQuestions = quizDeckRef.current.length;
        if (
          totalQuestions > 0 &&
          questionsAnsweredRef.current >= totalQuestions
        ) {
          // Don't end instantly — arm a finish line and let the player race to it.
          finishArmedRef.current = true;
          finishCrossedRef.current = false;
          await openRaceAlert({
            variant: "correct",
            title: "Final stretch!",
            message: "All questions done — race to the finish line!",
            autoMs: 1500,
          });
          const track = trackRef.current;
          if (track) spawnFinishLine(track);
          engageNitro();
          resumeRace();
          return;
        }

        resumeRace();
      } finally {
        setSubmitting(false);
      }
    },
    [
      quizQuestion,
      selectedAnswer,
      submitting,
      resumeRace,
      openRaceAlert,
      themeData,
      engageNitro,
      syncCarFx,
    ]
  );

  useEffect(() => {
    const steerKeys = [
      "ArrowUp",
      "ArrowDown",
      "ArrowLeft",
      "ArrowRight",
      "w",
      "W",
      "a",
      "A",
      "s",
      "S",
      "d",
      "D",
    ];
    const onKeyDown = (ev) => {
      if (steerKeys.includes(ev.key)) {
        ev.preventDefault();
        keysRef.current[ev.key] = true;
      }
      if (ev.code === "Space" || ev.key === " ") {
        // Read refs so this effect never re-binds (re-bind cleanup used to kill the race loop).
        if (
          quizOpenRef.current ||
          endScreenRef.current ||
          helpOpenRef.current ||
          !gameStartedRef.current
        ) {
          return;
        }
        // Ignore OS key-repeat so hold does not flicker nitro on/off.
        if (ev.repeat) return;
        ev.preventDefault();
        activateNitroRef.current();
      }
    };
    const onKeyUp = (ev) => {
      if (steerKeys.includes(ev.key)) {
        ev.preventDefault();
        keysRef.current[ev.key] = false;
      }
    };
    document.addEventListener("keydown", onKeyDown);
    document.addEventListener("keyup", onKeyUp);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.removeEventListener("keyup", onKeyUp);
      stopLoop();
      stopAllRaceSounds();
    };
  }, [stopLoop]);

  /** Portrait mobile: swipe / drag on the track + tap nitro. */
  useEffect(() => {
    const stage = stageRootRef.current;
    if (!stage || !gameStarted) return undefined;

    const blockedTarget = (target) => {
      if (!(target instanceof Element)) return true;
      return Boolean(
        target.closest(
          ".race-stage__mobile, .race-stage__overlay, .race-stage__start, .race-nitro, button, a, input"
        )
      );
    };

    const canControl = () =>
      gameStartedRef.current &&
      !quizOpenRef.current &&
      !endScreenRef.current &&
      !helpOpenRef.current &&
      !stage1CompleteRef.current &&
      playerRef.current.start;

    const onTouchStart = (ev) => {
      if (!canControl() || ev.touches.length !== 1) return;
      if (blockedTarget(ev.target)) return;
      const t = ev.touches[0];
      touchStartRef.current = {
        x: t.clientX,
        y: t.clientY,
        t: Date.now(),
        steered: false,
      };
    };

    /** One swipe = one adjacent lane. Finger position on the road is ignored. */
    const applySwipeStep = (start, dx) => {
      if (!start || start.steered) return false;
      start.steered = true;
      requestLaneStep(playerRef.current, dx < 0 ? -1 : 1);
      touchSteerRef.current = 0;
      syncSteerAxis();
      return true;
    };

    const onTouchMove = (ev) => {
      if (!canControl() || !touchStartRef.current || ev.touches.length !== 1) {
        return;
      }
      // Swipe-steer only in swipe mode on mobile portrait.
      if (
        isMobilePortraitRef.current &&
        mobileControlRef.current !== MOBILE_CONTROL_SWIPE
      ) {
        return;
      }
      const t = ev.touches[0];
      const start = touchStartRef.current;
      const dx = t.clientX - start.x;
      const dy = t.clientY - start.y;

      if (
        Math.abs(dx) >= SWIPE_STEER_PX &&
        Math.abs(dx) >= Math.abs(dy) * 0.65
      ) {
        ev.preventDefault();
        applySwipeStep(start, dx);
      }
    };

    const onTouchEnd = (ev) => {
      const start = touchStartRef.current;
      touchStartRef.current = null;
      touchSteerRef.current = 0;
      syncSteerAxis();
      if (!canControl() || !start) return;

      const touch = ev.changedTouches?.[0];
      if (!touch) return;
      const dx = touch.clientX - start.x;
      const dy = touch.clientY - start.y;
      const dt = Date.now() - start.t;
      const mobile = isMobilePortraitRef.current;

      // Mobile portrait: tap road → nitro (no center N button).
      if (
        mobile &&
        Math.abs(dx) < 22 &&
        Math.abs(dy) < 22 &&
        dt < 320 &&
        !start.steered
      ) {
        activateNitroRef.current();
        return;
      }

      // Quick swipe up → nitro (all modes)
      if (
        dy <= -SWIPE_NITRO_DY &&
        Math.abs(dy) > Math.abs(dx) * 1.1 &&
        dt < 500
      ) {
        activateNitroRef.current();
        return;
      }

      // Flick left/right: move exactly one of the 3 lanes
      if (
        (!mobile || mobileControlRef.current === MOBILE_CONTROL_SWIPE) &&
        Math.abs(dx) >= SWIPE_STEER_PX &&
        Math.abs(dx) > Math.abs(dy)
      ) {
        applySwipeStep(start, dx);
      }
    };

    const onTouchCancel = () => {
      touchStartRef.current = null;
      touchSteerRef.current = 0;
      syncSteerAxis();
    };

    stage.addEventListener("touchstart", onTouchStart, { passive: true });
    stage.addEventListener("touchmove", onTouchMove, { passive: false });
    stage.addEventListener("touchend", onTouchEnd, { passive: true });
    stage.addEventListener("touchcancel", onTouchCancel, { passive: true });
    return () => {
      stage.removeEventListener("touchstart", onTouchStart);
      stage.removeEventListener("touchmove", onTouchMove);
      stage.removeEventListener("touchend", onTouchEnd);
      stage.removeEventListener("touchcancel", onTouchCancel);
      touchSteerRef.current = 0;
      syncSteerAxis();
    };
  }, [gameStarted, syncSteerAxis]);

  /** Portrait mobile: tilt phone left/right to steer (motion mode only). */
  useEffect(() => {
    if (
      !tiltEnabled ||
      !gameStarted ||
      mobileControl !== MOBILE_CONTROL_MOTION
    ) {
      tiltSteerRef.current = 0;
      tiltTargetRef.current = 0;
      syncSteerAxis();
      return undefined;
    }

    const onOrient = (ev) => {
      if (
        !gameStartedRef.current ||
        quizOpenRef.current ||
        endScreenRef.current ||
        helpOpenRef.current ||
        mobileControlRef.current !== MOBILE_CONTROL_MOTION
      ) {
        tiltTargetRef.current = 0;
        tiltSteerRef.current = 0;
        syncSteerAxis();
        return;
      }

      // Portrait: gamma is left/right tilt. Fallback to beta when sideways.
      const portrait =
        typeof window !== "undefined" &&
        window.matchMedia?.("(orientation: portrait)").matches;
      let raw = 0;
      if (portrait) {
        raw = Number(ev.gamma) || 0;
      } else {
        raw = Number(ev.beta) || 0;
      }

      let target = 0;
      if (Math.abs(raw) >= TILT_DEADZONE_DEG) {
        const sign = raw < 0 ? -1 : 1;
        const linear = Math.min(
          1,
          (Math.abs(raw) - TILT_DEADZONE_DEG) /
            (TILT_FULL_DEG - TILT_DEADZONE_DEG)
        );
        // Power < 1 → small tilts bite harder / feel more dynamic.
        const mag = Math.pow(linear, 0.58);
        target = sign * mag;
      }
      tiltTargetRef.current = target;
    };

    window.addEventListener("deviceorientation", onOrient, true);
    return () => {
      window.removeEventListener("deviceorientation", onOrient, true);
      tiltTargetRef.current = 0;
      tiltSteerRef.current = 0;
      syncSteerAxis();
    };
  }, [tiltEnabled, gameStarted, mobileControl, syncSteerAxis]);

  /** Demo users: after 10s of play, show plans prompt (same flow as restore_indias_legacy). */
  useEffect(() => {
    if (!isDemoBypass || !gameStarted || themeDataBlocked) return undefined;
    if (demoRedirectTriggeredRef.current) return undefined;
    const t = window.setTimeout(() => {
      redirectDemoToPlans();
    }, DEMO_REDIRECT_MS);
    return () => window.clearTimeout(t);
  }, [isDemoBypass, gameStarted, themeDataBlocked, redirectDemoToPlans]);

  useEffect(() => {
    if (!dbReady || themeDataBlocked || !gameStarted) return undefined;
    if (!sessionStartRef.current) {
      sessionStartRef.current = Date.now();
      timerStartedRef.current = true;
    }
    const tick = () => {
      if (!sessionStartRef.current || stage1CompleteRef.current) return;
      const pauseHold = pausedAtRef.current
        ? Date.now() - pausedAtRef.current
        : 0;
      const live = Math.floor(
        (Date.now() -
          sessionStartRef.current -
          pausedTotalRef.current -
          pauseHold) /
          1000
      );
      const total = baseElapsedRef.current + Math.max(0, live);
      elapsedRef.current = total;
      // Prefer imperative HUD so React does not thrash during the race.
      if (hudTimeRef.current) {
        hudTimeRef.current.textContent = formatSecondsToClock(total);
      } else {
        setElapsedSec(total);
      }
    };
    tick();
    const id = setInterval(tick, 250);
    return () => clearInterval(id);
  }, [dbReady, themeDataBlocked, gameStarted]);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      if (!themeData) return;

      const mode = readGameMode();
      gameModeRef.current = mode;
      if (!cancelled) setGameMode(mode);

      const validation = validateThemeStageData(themeData, { mode });
      if (!validation.ok) {
        setThemeDataBlocked(true);
        if (!themeMissingPopupShownRef.current) {
          themeMissingPopupShownRef.current = true;
          await fireThemeStageDataMissingSwal(validation.missing);
        }
        return;
      }
      setThemeDataBlocked(false);

      if (isDemoBypass) {
        setDbReady(true);
        return;
      }

      if (!backendBase || !adminToken) {
        setDbReady(true);
        return;
      }

      try {
        const uid = storedUser.userId || storedUser.userid || storedUser.id;
        const sessionId = storedUser.sessionId;
        const organizationId = storedUser.organizationId;
        if (!uid || !sessionId || !organizationId) {
          setDbReady(true);
          return;
        }

        let stageId = stageRowId;
        if (!stageId) {
          const startRes = await fetch(`${backendBase}/welcomeStageStart`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              email: storedUser.email || "",
              emp_id: storedUser.employeeId || storedUser.emp_id || "",
              name: storedUser.name || storedUser.fullName || "",
              user_type: storedUser.role || storedUser.user_type || "",
            }),
          });
          const startJson = await startRes.json().catch(() => ({}));
          stageId = startJson?.id || startJson?.stageId || null;
          if (stageId) {
            sessionStorage.setItem(STAGE_ROW_KEY, String(stageId));
            if (!cancelled) setStageRowId(String(stageId));
          }
        }

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            id: stageId,
          }),
        });
        const stageJson = await stageRes.json().catch(() => ({}));
        const st = stageJson?.data || stageJson?.stage || stageJson;

        if (st && typeof st === "object") {
          hydrateCompletedStagesFromRecord(st);
          const endlessReplay = isEndlessMode(mode);
          const redirect = getRedirectForPlayedStage(st, STAGE_NUM, {
            endlessReplay,
          });
          if (redirect && redirect !== "/stage1") {
            if (!cancelled) navigate(redirect, { replace: true });
            return;
          }

          // stage1 = quiz score, stage2 = endless best; total_score is their sum.
          // Each mode's HUD / best only reflects its own column.
          const dbStage1 = Number(st[`stage${QUIZ_SCORE_STAGE}`]) || 0;
          const dbEndlessBest = Number(st[`stage${ENDLESS_SCORE_STAGE}`]) || 0;
          const best = isEndlessMode(mode) ? dbEndlessBest : dbStage1;
          otherStagesTotalRef.current = 0;
          if (!cancelled) {
            setBestScore(best);
            bestScoreRef.current = best;
            if (isEndlessMode(mode)) {
              setStage1Points(0);
              pointsRef.current = 0;
              const used = Math.max(
                0,
                Math.floor(Number(st.currentQues) || 0)
              );
              playsUsedRef.current = used;
              setPlaysUsed(used);
              const maxPlays = getEndlessLifelinePlays(themeData);
              maxPlaysRef.current = maxPlays;
              if (maxPlays > 0 && used >= maxPlays) {
                setEndScreen({
                  score: best,
                  bestScore: best,
                  distance: 0,
                  survivalSec: 0,
                  reason: "no_lives",
                  canReplay: false,
                });
              }
            } else {
              setStage1Points(dbStage1);
              pointsRef.current = dbStage1;
            }
          }

          if (!isEndlessMode(mode)) {
            // Quiz is one-shot and only saves on finish, so it always starts at 00:00.
            // Do not seed from stages.time — that column is shared with endless and
            // would add the endless run's time onto the quiz time.
            baseElapsedRef.current = 0;
            elapsedRef.current = 0;
            if (!cancelled) setElapsedSec(0);
          }
        }
      } catch (err) {
        console.error("Stage1 bootstrap:", err);
      } finally {
        if (!cancelled) setDbReady(true);
      }
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, [
    themeData,
    isDemoBypass,
    backendBase,
    adminToken,
    storedUser,
    stageRowId,
    navigate,
  ]);

  const setHoldSteer = useCallback(
    (dir) => {
      if (dir === "left") touchSteerRef.current = -1;
      else if (dir === "right") touchSteerRef.current = 1;
      else touchSteerRef.current = 0;
      syncSteerAxis();
    },
    [syncSteerAxis]
  );

  const enableTiltControls = useCallback(async () => {
    if (tiltEnabledRef.current) {
      setTiltEnabled(false);
      tiltEnabledRef.current = false;
      tiltSteerRef.current = 0;
      tiltTargetRef.current = 0;
      syncSteerAxis();
      return false;
    }
    try {
      const DOE = window.DeviceOrientationEvent;
      if (DOE && typeof DOE.requestPermission === "function") {
        const result = await DOE.requestPermission();
        if (result !== "granted") {
          setTiltEnabled(false);
          return false;
        }
      }
      setTiltEnabled(true);
      tiltEnabledRef.current = true;
      return true;
    } catch {
      setTiltEnabled(false);
      return false;
    }
  }, [syncSteerAxis]);

  const applyMobileControlMode = useCallback(
    async (modeId) => {
      const next = MOBILE_CONTROL_OPTIONS.some((o) => o.id === modeId)
        ? modeId
        : MOBILE_CONTROL_SWIPE;
      writeSessionValue(MOBILE_CONTROL_STORAGE_KEY, next);
      mobileControlRef.current = next;
      setMobileControl(next);
      touchSteerRef.current = 0;

      if (next === MOBILE_CONTROL_MOTION) {
        if (!tiltEnabledRef.current) {
          const ok = await enableTiltControls();
          if (!ok) {
            writeSessionValue(MOBILE_CONTROL_STORAGE_KEY, MOBILE_CONTROL_SWIPE);
            mobileControlRef.current = MOBILE_CONTROL_SWIPE;
            setMobileControl(MOBILE_CONTROL_SWIPE);
          }
        }
      } else if (tiltEnabledRef.current) {
        setTiltEnabled(false);
        tiltEnabledRef.current = false;
        tiltSteerRef.current = 0;
        tiltTargetRef.current = 0;
      }
      syncSteerAxis();
    },
    [enableTiltControls, syncSteerAxis]
  );

  const handleHelp = async () => {
    if (helpOpenRef.current) return;
    const shouldPause =
      gameStartedRef.current &&
      !endScreenRef.current &&
      !stage1CompleteRef.current &&
      !submittedRef.current;

    if (shouldPause) pauseRaceForHelp();

    try {
      await fireStageHelpSwal({
        themeData,
        rules: howToPlayRules,
        title: "How to play",
        onOpen: () => {
          if (shouldPause) pauseRaceForHelp();
        },
        onClose: () => {
          if (shouldPause) resumeRaceAfterHelp();
        },
      });
    } catch {
      if (shouldPause) resumeRaceAfterHelp();
    }
  };

  if (!dbReady && !themeDataBlocked) {
    return (
      <div className="race-page">
        <p className="race-page__status">Loading race…</p>
      </div>
    );
  }

  if (themeDataBlocked) {
    return (
      <div className="race-page">
        <p className="race-page__status">
          Required game data is missing. Add road, car, and obstacle images
          {isEndless ? "" : ", plus at least one quiz question,"} in Admin →
          Theme → Game Content, then reload.
        </p>
      </div>
    );
  }

  const quizOptions = quizQuestion
    ? [
        quizQuestion.option_one,
        quizQuestion.option_two,
        quizQuestion.option_three,
        quizQuestion.option_four,
      ].filter(Boolean)
    : [];

  const pointsLabel = String(Math.max(0, Math.floor(displayTotalScore)));
  const quizQuestionNum = questionsAnsweredRef.current + 1;
  const quizQuestionTotal = Math.max(
    quizDeckRef.current.length,
    quizList.length,
    1
  );
  const optionLetters = ["A", "B", "C", "D"];
  const showArrowPad =
    isMobilePortrait &&
    mobileControl === MOBILE_CONTROL_ARROWS &&
    gameStarted &&
    !endScreen &&
    !countdownLabel;
  const mobileHint =
    mobileControl === MOBILE_CONTROL_ARROWS
      ? "Hold ← → · Space = Nitro on/off"
      : mobileControl === MOBILE_CONTROL_MOTION
        ? "Tilt phone · Tap road = Nitro on/off"
        : "Swipe road · Tap road = Nitro on/off";

  const mobileControlPicker = isMobilePortrait ? (
    <div className="race-control-picker" role="group" aria-label="Mobile controls">
      <p className="race-control-picker__title">Choose controls</p>
      <div className="race-control-picker__row">
        {MOBILE_CONTROL_OPTIONS.map((opt) => {
          if (opt.id === MOBILE_CONTROL_MOTION && !tiltSupported) return null;
          const selected = mobileControl === opt.id;
          return (
            <button
              key={opt.id}
              type="button"
              className={`race-control-picker__btn${selected ? " is-selected" : ""}`}
              onClick={() => applyMobileControlMode(opt.id)}
              aria-pressed={selected}
            >
              <span className="race-control-picker__label">{opt.label}</span>
              <span className="race-control-picker__hint">{opt.hint}</span>
            </button>
          );
        })}
      </div>
      <p className="race-control-picker__nitro">
        Space / tap road to toggle Nitro · clears obstacles to refill
      </p>
    </div>
  ) : null;

  return (
    <div
      className="race-page"
      style={
        gameTextColor
          ? { "--race-game-text": gameTextColor }
          : undefined
      }
    >
      <div
        className={`race-hud${isEndless ? " race-hud--endless" : ""}`}
        aria-label="Race stats"
      >
        <div className="race-hud__cluster race-hud__cluster--left">
          <div className="race-meter race-meter--time">
            <span className="race-meter__icon">
              <img
                src={timeHudIcon}
                alt=""
                className="race-meter__art"
                draggable={false}
              />
              <span
                className="race-meter__value"
                ref={hudTimeRef}
                aria-label={`Time ${formatSecondsToClock(elapsedSec)}`}
              >
                {formatSecondsToClock(elapsedSec)}
              </span>
            </span>
          </div>

          <button
            type="button"
            className="race-meter race-meter--rules"
            onClick={handleHelp}
            aria-label="How to play"
          >
            <span className="race-meter__icon">
              <img
                src={rulesHudIcon}
                alt=""
                className="race-meter__art"
                draggable={false}
              />
            </span>
          </button>
        </div>

        <div className="race-hud__track-gap" aria-hidden="true" />

        <div className="race-hud__cluster race-hud__cluster--right">
          {isEndless ? (
            <div className="race-meter race-meter--dist">
              <span className="race-meter__icon">
                <img
                  src={timeHudIcon}
                  alt=""
                  className="race-meter__art"
                  draggable={false}
                />
                <span
                  className="race-meter__value"
                  ref={hudDistRef}
                  aria-label="Distance"
                >
                  0m
                </span>
              </span>
            </div>
          ) : null}

          {showLivesHud ? (
            <div className="race-meter race-meter--lives" aria-label="Lives">
              <span className="race-meter__icon">
                <img
                  src={livesHudIcon}
                  alt=""
                  className="race-meter__art"
                  draggable={false}
                />
                <span
                  className="race-meter__value"
                  ref={hudLivesRef}
                  aria-label="Lives"
                >
                  {Math.max(0, maxLifelinePlays - playsUsed)}
                </span>
              </span>
            </div>
          ) : null}

          <div className="race-meter race-meter--points">
            <span className="race-meter__icon">
              <img
                src={pointsHudIcon}
                alt=""
                className="race-meter__art"
                draggable={false}
              />
              <span
                className="race-meter__value"
                ref={hudScoreRef}
                aria-label="Points"
              >
                {pointsLabel}
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className="race-stage" ref={stageRootRef}>
        <div
          className="race-stage__game-area1"
          style={
            raceAssets.roadUrl
              ? { backgroundImage: `url("${raceAssets.roadUrl}")` }
              : undefined
          }
        >
          <RaceTrack ref={trackRef} />

          {gameStarted && !endScreen && !countdownLabel ? (
            <div
              ref={nitroRootRef}
              className="race-nitro race-nitro--road is-ready"
              aria-label="Nitro"
              style={{ ["--nitro-pct"]: "100%" }}
            >
              <div className="race-nitro__label" ref={nitroLabelRef}>
                NITRO
              </div>
              <div className="race-nitro__bar">
                <div
                  ref={nitroFillRef}
                  className="race-nitro__fill"
                  style={{ ["--nitro-pct"]: "100%" }}
                />
              </div>
            </div>
          ) : null}
        </div>

        <div className={`race-stage__start${gameStarted || endScreen ? " hide" : ""}`}>
          <div className="race-start-card">
            <div className="race-start-tips">
              <p className="race-start-tips__title">HOW TO PLAY</p>
              <ul className="race-start-tips__list">
                {howToPlayRules.map((rule) => (
                  <li key={rule}>{rule}</li>
                ))}
              </ul>
            </div>

            {mobileControlPicker}

            <button
              type="button"
              className="race-start-play"
              style={playButtonStyle}
              onClick={startRace}
            >
              <span className="race-start-play__glow" aria-hidden />
              <span className="race-start-play__text">PLAY</span>
            </button>
          </div>
        </div>

        {showArrowPad ? (
          <div className="race-stage__mobile" aria-label="Arrow controls">
            <button
              type="button"
              className="race-stage__mobile-btn"
              aria-label="Steer left"
              onPointerDown={(e) => {
                e.preventDefault();
                e.currentTarget.setPointerCapture?.(e.pointerId);
                setHoldSteer("left");
              }}
              onPointerUp={() => setHoldSteer(null)}
              onPointerLeave={() => setHoldSteer(null)}
              onPointerCancel={() => setHoldSteer(null)}
            >
              ‹
            </button>
            <button
              type="button"
              className="race-stage__mobile-btn"
              aria-label="Steer right"
              onPointerDown={(e) => {
                e.preventDefault();
                e.currentTarget.setPointerCapture?.(e.pointerId);
                setHoldSteer("right");
              }}
              onPointerUp={() => setHoldSteer(null)}
              onPointerLeave={() => setHoldSteer(null)}
              onPointerCancel={() => setHoldSteer(null)}
            >
              ›
            </button>
          </div>
        ) : null}
        {isMobilePortrait && gameStarted && !endScreen && !countdownLabel ? (
          <p className="race-stage__mobile-hint" aria-hidden="true">
            {mobileHint}
          </p>
        ) : null}

        {countdownLabel ? (
          <div className="race-countdown" aria-live="assertive">
            {countdownLabel}
          </div>
        ) : null}

        {endScreen ? (
          <div className="race-stage__overlay race-stage__overlay--quiz">
            <div className="race-end" role="dialog" aria-modal="true">
              <div className="race-end__badge">Scoreboard</div>
              <div className="race-end__stats">
                <div className="race-end__row race-end__row--score is-highlight">
                  <span>Score</span>
                  <span>{Math.floor(endScreen.score).toLocaleString()}</span>
                </div>
                <div className="race-end__row race-end__row--best">
                  <span>Best</span>
                  <span>
                    {Math.floor(endScreen.bestScore).toLocaleString()}
                  </span>
                </div>
                <div className="race-end__row">
                  <span>Distance</span>
                  <span>{Math.floor(endScreen.distance)}m</span>
                </div>
                <div className="race-end__row">
                  <span>Survival</span>
                  <span>{formatSecondsToClock(endScreen.survivalSec)}</span>
                </div>
                {maxLifelinePlays > 0 ? (
                  <div className="race-end__row">
                    <span>Lives</span>
                    <span>
                      {Math.max(0, maxLifelinePlays - playsUsed)} left
                    </span>
                  </div>
                ) : null}
              </div>
              <div className="race-end__controls">
                {isMobilePortrait ? mobileControlPicker : null}
              </div>
              <div className="race-end__actions">
                {endScreen.canReplay !== false ? (
                <button
                  type="button"
                  className="race-end__btn race-end__btn--primary"
                  onClick={replayEndless}
                >
                  Replay
                </button>
                ) : null}
                <button
                  type="button"
                  className="race-end__btn"
                  onClick={() =>
                    navigate("/thankyou", {
                      replace: true,
                      state: {
                        points: Math.floor(endScreen.score),
                        time: formatSecondsToClock(endScreen.survivalSec),
                      },
                    })
                  }
                >
                  Home
                </button>
              </div>
            </div>
          </div>
        ) : null}

        {raceAlert ? (
          <div className="race-stage__overlay race-stage__overlay--quiz">
            <div
              className={`race-alert race-alert--${raceAlert.variant}`}
              role="dialog"
              aria-modal="true"
              aria-labelledby="race-alert-title"
            >
              <div className="race-alert__badge">
                <QuizFlagIcon />
                <span id="race-alert-title">{raceAlert.title}</span>
                <QuizFlagIcon />
              </div>
              <p className="race-alert__message">{raceAlert.message}</p>
              {raceAlert.confirmLabel ? (
                <div className="race-alert__actions">
                  <button
                    type="button"
                    className="race-alert__btn"
                    onClick={closeRaceAlert}
                  >
                    {raceAlert.confirmLabel}
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        ) : null}

        {quizOpen && quizQuestion ? (
          <div className="race-stage__overlay race-stage__overlay--quiz">
            <div
              className="race-quiz"
              role="dialog"
              aria-modal="true"
              aria-labelledby="race-quiz-title"
            >
              <div className="race-quiz__badge">
                <QuizFlagIcon />
                <span id="race-quiz-title">
                  QUESTION {quizQuestionNum} / {quizQuestionTotal}
                </span>
                <QuizFlagIcon />
              </div>

              <p className="race-quiz__prompt">{quizQuestion.question_name}</p>

              <div
                className={`race-quiz__options${
                  quizOptions.length === 2
                    ? " race-quiz__options--two"
                    : quizOptions.length === 3
                      ? " race-quiz__options--three"
                      : quizOptions.length === 1
                        ? " race-quiz__options--one"
                        : ""
                }`}
              >
                {quizOptions.map((opt, i) => {
                  const letter = optionLetters[i] || String(i + 1);
                  const selected = selectedAnswer === opt;
                  return (
                    <button
                      key={`${letter}-${opt}`}
                      type="button"
                      className={`race-quiz__option${
                        selected ? " is-selected" : ""
                      }`}
                      disabled={submitting}
                      onClick={() => handleQuizSubmit(opt)}
                    >
                      <span className="race-quiz__letter">{letter}</span>
                      <span className="race-quiz__text">{opt}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}
